import { MarkEmailRead, Visibility } from "@mui/icons-material";
import {
    Box,
    IconButton,
    MenuItem,
    TextField,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DateTimeLabel } from "../../components/DateTimeLabel";
import { HeaderPage } from "../../components/Header/HeaderPage";
import PageWrapper from "../../components/PageWrapper";
import TableDataGrid from "../../components/TableDataGrid";
import { useAuth } from "../../contexts/contexts";
import { usePaginatedData } from "../../hooks/usePaginatedData";
import { EmailLog } from "../../interfaces/emailLog";
import { EmailLogDetailsModal } from "./EmailLogDetailsModal";
import { EmailLogStatusChip, emailLogSource } from "./helpers";

const PERMISSION = "emails.historico";

export default function EmailLogsPage() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { permissions, hasPermissions } = useAuth();

    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [selectedId, setSelectedId] = useState<number | null>(null);

    useEffect(() => {
        const timeout = setTimeout(() => setSearch(searchInput.trim()), 400);

        return () => clearTimeout(timeout);
    }, [searchInput]);

    const endpoint = useMemo(() => {
        const params = new URLSearchParams();
        if (search) params.append("search", search);
        if (status) params.append("status", status);
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);

        const query = params.toString();

        return query ? `/email-logs?${query}` : "/email-logs";
    }, [search, status, startDate, endDate]);

    const { data, isLoading, setPage, meta } =
        usePaginatedData<EmailLog>(endpoint);

    useEffect(() => {
        setPage(1);
    }, [endpoint]);

    useEffect(() => {
        if (hasPermissions(PERMISSION) === false && permissions.length > 0) {
            navigate("/Error");
        }
    }, [permissions]);

    const columns: GridColDef<EmailLog>[] = [
        {
            field: "created_at",
            headerName: "Data/Hora",
            sortable: false,
            minWidth: 130,
            flex: 0.8,
            renderCell: ({ value }) => <DateTimeLabel value={value} />,
        },
        {
            field: "recipients",
            headerName: "Destinatário",
            sortable: false,
            minWidth: 180,
            flex: 1.2,
        },
        {
            field: "subject",
            headerName: "Assunto",
            sortable: false,
            minWidth: 180,
            flex: 1.5,
        },
        {
            field: "source",
            headerName: "Origem",
            sortable: false,
            minWidth: 140,
            flex: 0.9,
            valueGetter: ({ row }) => emailLogSource(row),
        },
        {
            field: "status",
            headerName: "Status",
            sortable: false,
            minWidth: 110,
            flex: 0.6,
            renderCell: ({ row }) => <EmailLogStatusChip status={row.status} />,
        },
        {
            field: "actions",
            headerName: "",
            sortable: false,
            disableExport: true,
            align: "right",
            width: 60,
            renderCell: ({ row }) => (
                <IconButton
                    title="Ver e-mail"
                    onClick={(event) => {
                        event.stopPropagation();
                        setSelectedId(row.id);
                    }}
                >
                    <Visibility color="primary" />
                </IconButton>
            ),
        },
    ];

    return (
        <PageWrapper>
            <Box mb={3}>
                <HeaderPage
                    title="Histórico de e-mails"
                    subtitle="Todos os e-mails enviados pelo sistema, com status e conteúdo"
                    icon={<MarkEmailRead />}
                />
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        md: "2fr 1fr 1fr 1fr",
                    },
                    gap: 2,
                    mb: 3,
                }}
            >
                <TextField
                    size="small"
                    label="Buscar por destinatário ou assunto"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                />
                <TextField
                    select
                    size="small"
                    label="Status"
                    value={status}
                    onChange={(event) => setStatus(event.target.value)}
                >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="sent">Enviado</MenuItem>
                    <MenuItem value="failed">Falhou</MenuItem>
                    <MenuItem value="sending">Enviando</MenuItem>
                </TextField>
                <TextField
                    size="small"
                    type="date"
                    label="De"
                    InputLabelProps={{ shrink: true }}
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                />
                <TextField
                    size="small"
                    type="date"
                    label="Até"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: startDate || undefined }}
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                />
            </Box>

            <TableDataGrid
                rows={data}
                columns={columns}
                loading={isLoading}
                autoHeight
                hideColumnMenu
                hideExports
                hideFilter
                toolBar={false}
                paginationMode="server"
                rowCount={meta.total}
                page={meta.current_page - 1}
                onPageChange={(newPage) => setPage(newPage + 1)}
                pageSize={meta.per_page}
                onRowClick={({ row }) => setSelectedId(row.id)}
                columnVisibilityModel={{ source: !isMobile }}
                sx={{ cursor: "pointer" }}
            />

            <EmailLogDetailsModal
                emailLogId={selectedId}
                onClose={() => setSelectedId(null)}
            />
        </PageWrapper>
    );
}
